import type { CsReviewProposal } from '@sbc/domain-models/agents';
import { Check, X } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ApiError } from '@/lib/api/client';
import { useDecideCsReviewProposal } from '@/lib/api/cs-reviews';
import { useAuth } from '@/lib/auth/auth-context';

function TextBlock({ label, text }: { label: string; text: string }) {
  return (
    <div className="min-w-0 flex-1 space-y-1">
      <p className="text-xs font-medium text-[var(--color-muted-foreground)]">{label}</p>
      <pre className="max-h-80 overflow-auto rounded-md border bg-[var(--color-muted)] p-2 text-xs whitespace-pre-wrap">
        {text || '—'}
      </pre>
    </div>
  );
}

function decisionLabel(proposal: CsReviewProposal): string | null {
  if (!proposal.decision) {
    return null;
  }
  const who = proposal.decision.decidedByEmail ?? 'someone';
  const when = new Date(proposal.decision.decidedAt).toLocaleString();
  return `${proposal.decision.decision} by ${who} · ${when}`;
}

export function ProposalCard({ proposal }: { proposal: CsReviewProposal }) {
  const { user } = useAuth();
  const decide = useDecideCsReviewProposal();
  const [error, setError] = useState<string | null>(null);

  const run = (action: 'confirm' | 'approve' | 'reject') => {
    setError(null);
    decide.mutate(
      { id: proposal.id, action },
      {
        onError: (caught) =>
          setError(caught instanceof ApiError ? caught.message : 'The action failed.'),
      },
    );
  };

  const decided = decisionLabel(proposal);
  const isKb = proposal.kind === 'kb-edit-proposal';
  const canApply = isKb || user?.role === 'admin';

  return (
    <Card>
      <CardHeader className="space-y-1">
        <CardTitle className="flex flex-wrap items-center gap-2 text-base">
          {isKb ? (
            <>
              <Badge>{proposal.payload.articleId === null ? 'New article' : 'KB edit'}</Badge>
              <span className="font-mono text-sm">{proposal.payload.articlePath}</span>
            </>
          ) : (
            <>
              <Badge>Prompt rule</Badge>
              <span className="font-mono text-sm">{proposal.payload.agentSlug}</span>
            </>
          )}
        </CardTitle>
        <p className="text-sm text-[var(--color-muted-foreground)]">{proposal.payload.rationale}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {isKb ? (
          <div className="flex flex-col gap-3 md:flex-row">
            <TextBlock label="Before" text={proposal.payload.before} />
            <TextBlock label="After" text={proposal.payload.after} />
          </div>
        ) : (
          <TextBlock label="Rule appended to the drafter prompt" text={proposal.payload.rule} />
        )}
        <TextBlock
          label="Redraft with this change"
          text={proposal.payload.redraft ?? 'No redraft yet. Ask the reviewer to redraft.'}
        />
        {error && <p className="text-sm text-[var(--color-destructive)]">{error}</p>}
        {decided ? (
          <p className="text-sm text-[var(--color-muted-foreground)]">{decided}</p>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            {canApply && (
              <Button onClick={() => run(isKb ? 'confirm' : 'approve')} disabled={decide.isPending}>
                <Check className="size-4" />
                {isKb ? 'Confirm and publish' : 'Approve'}
              </Button>
            )}
            <Button variant="outline" onClick={() => run('reject')} disabled={decide.isPending}>
              <X className="size-4" />
              Reject
            </Button>
            {!canApply && (
              <span className="text-xs text-[var(--color-muted-foreground)]">
                An engineer approves prompt rules on Proposals.
              </span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
